import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X, Copy, Check, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const EASE = [0.16, 1, 0.3, 1];
const COUPON_CODE = "STAY15";

/**
 * ExitIntentModal — "Wait, don't go" sheet with a direct coupon code.
 * No email required. One-time-per-device is managed by useExitIntent.
 */
export default function ExitIntentModal({ open, onClose }) {
  const { t, lang } = useLanguage();
  const reduce = useReducedMotion();
  const [copied, setCopied] = useState(false);

  // Body scroll lock (identical technique to LeadCapture).
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const prev = { position: document.body.style.position, top: document.body.style.top, width: document.body.style.width };
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.dataset.scrollLocked = "1";
    document.body.setAttribute("data-exit-modal-open", "1");
    return () => {
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.width = prev.width;
      delete document.body.dataset.scrollLocked;
      document.body.removeAttribute("data-exit-modal-open");
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (_e) { /* clipboard blocked */ }
  };

  const handleShopClick = () => {
    onClose();
    // Smooth-scroll to the main collection.
    window.setTimeout(() => {
      const el = document.getElementById("collection") || document.getElementById("shop");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="exit-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.28, ease: EASE }}
            onClick={onClose}
            className="fixed inset-0 z-[85] bg-black/50 backdrop-blur-sm"
            aria-hidden="true"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-title"
            data-testid="exit-modal"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 32, scale: 0.98 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.36, ease: EASE }}
            className="fixed inset-x-0 bottom-0 z-[86] flex justify-center sm:inset-0 sm:items-center sm:p-6"
          >
            <div
              className="pointer-events-auto w-full max-w-[540px] overflow-hidden rounded-t-3xl bg-white shadow-[0_-24px_80px_-20px_rgba(0,0,0,0.4)] sm:rounded-3xl"
              style={{ paddingBottom: "max(24px, env(safe-area-inset-bottom))" }}
            >
              {/* Drag handle (mobile) */}
              <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-[#e5e5e0] sm:hidden" aria-hidden="true" />

              {/* Top luxe strip */}
              <div className="relative bg-[#111] px-6 py-4 text-white">
                <button
                  onClick={onClose}
                  aria-label={lang === "es" ? "Cerrar" : "Close"}
                  data-testid="exit-close"
                  className="press absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <X className="h-5 w-5" strokeWidth={1.5} />
                </button>
                <div className="flex items-center gap-2 pr-8">
                  <Sparkles className="h-4 w-4 text-white/85" strokeWidth={1.6} />
                  <p className="font-body text-[10px] uppercase tracking-[0.24em] text-white/75">
                    {lang === "es" ? "Oferta exclusiva de despedida" : "Exclusive parting offer"}
                  </p>
                </div>
                <h2
                  id="exit-title"
                  className="mt-1 font-serif-display text-2xl font-medium leading-tight sm:text-3xl"
                >
                  {lang === "es" ? "Espera… antes de irte." : "Wait… before you go."}
                </h2>
              </div>

              {/* Body */}
              <div className="px-6 py-6 sm:px-8">
                <p className="font-body text-sm leading-relaxed text-[#555]">
                  {lang === "es"
                    ? "Llévate un 15% de descuento en tu primer par. Sin registro, sin fricción — el código es tuyo."
                    : "Take 15% off your first pair. No sign-up, no friction — the code is yours."}
                </p>

                {/* Coupon block */}
                <div className="mt-5 flex items-center justify-between gap-3 rounded-2xl border border-[#111] bg-[#fafaf8] p-4">
                  <div className="min-w-0">
                    <p className="font-body text-[10px] uppercase tracking-[0.24em] text-[#999]">
                      {lang === "es" ? "Tu código" : "Your code"}
                    </p>
                    <span
                      data-testid="exit-code"
                      className="mt-0.5 block font-mono text-xl font-semibold tracking-[0.28em] text-[#111]"
                    >
                      {COUPON_CODE}
                    </span>
                  </div>
                  <button
                    onClick={handleCopy}
                    data-testid="exit-copy"
                    aria-label={lang === "es" ? "Copiar código" : "Copy code"}
                    className="press inline-flex items-center gap-1.5 rounded-full border border-[#111] px-3 py-1.5 font-body text-xs font-medium text-[#111] transition-colors hover:bg-[#111] hover:text-white"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3.5 w-3.5" strokeWidth={1.8} />
                        {lang === "es" ? "Copiado" : "Copied"}
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" strokeWidth={1.8} />
                        {lang === "es" ? "Copiar" : "Copy"}
                      </>
                    )}
                  </button>
                </div>

                <p className="mt-3 font-body text-[11px] leading-relaxed text-[#888]">
                  {lang === "es"
                    ? "Aplica al finalizar tu pedido. Válido en toda la colección. Un solo uso por persona."
                    : "Apply at checkout. Valid site-wide. One use per person."}
                </p>

                <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    onClick={handleShopClick}
                    data-testid="exit-shop-cta"
                    className="press inline-flex items-center justify-center rounded-full bg-[#111] px-5 py-3 font-body text-sm font-medium text-white transition-colors hover:bg-black"
                  >
                    {lang === "es" ? "Ver la colección" : "Shop the collection"}
                  </button>
                  <button
                    onClick={onClose}
                    data-testid="exit-dismiss"
                    className="press inline-flex items-center justify-center rounded-full border border-[#e2e2e0] bg-white px-5 py-3 font-body text-sm text-[#333] transition-colors hover:border-[#111]"
                  >
                    {lang === "es" ? "Ahora no" : "Not now"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
